import { ReactNode, useState, ReactElement, ComponentProps } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  IconButton,
  OnHoverFunction,
  Tooltip,
  TreeNode,
  useTreeMethodsContext,
} from "@fidelix/fx-miranda";
import styled from "styled-components";
import { useLingui } from "@lingui/react";
import { t } from "@lingui/core/macro";
import { getActiveRoute, MainRoute, mainRoutes } from "../common/nav-bar";
import {
  getDeviceBelindaUrl,
  getDeviceTooltipText,
  hasDeviceCapability,
} from "../device/device.utils";
import { useDeviceStateContext } from "../device/device-status-context";
import { hasPermission } from "../utils/permission-helper";
import { Permissions } from "../const";
import { useNavigatonPath, useRequiredParams } from "../utils/url";
import { DownloadGraphics } from "../graphics/download-graphics";
import { DeleteGraphics } from "../graphics/new-delete-graphics";
import { ApplyProcessTypeDialog } from "../graphics/apply-process-type-dialog";
import { useUserContext } from "../user/user-context";
import { DeviceTreeNode } from "./tree.queries";
import { DeviceCapabilities } from "../device/device.model";
import { GraphicCategoryType } from "../graphics/graphics-category.model";
import { featureFlags } from "../feature-flags/feature-flag.utils";

type XSmallIconButtonProps = Omit<
  ComponentProps<typeof IconButton>,
  "shape"
> & {
  shape: "xsmall";
};

function DeviceTooltip({ node }: { node: TreeNode }) {
  const { getDeviceState, getDevice } = useDeviceStateContext();
  const { i18n } = useLingui();
  const deviceId = node.getId();
  return (
    <>
      {getDeviceTooltipText(
        getDeviceState(deviceId),
        getDevice(deviceId).lastOnlineAt,
        i18n.locale,
      )}
    </>
  );
}

export const hoveredNode: OnHoverFunction = (node) => {
  const currentView = getActiveRoute(mainRoutes) as MainRoute;
  let buttons:
    | ReactElement<XSmallIconButtonProps>
    | ReactElement<{ children: ReactNode }>
    | undefined = undefined;
  let trailing: ReactNode = undefined;
  let tooltip: ReactNode = undefined;

  if (node.type === "device") {
    tooltip = <DeviceTooltip node={node} />;
    buttons = <DeviceHover node={node as DeviceTreeNode} />;
  }

  if (currentView !== "graphics") {
    // visible to every UI except graphics because they show nothing on hover
    return { buttons, trailing, tooltip };
  }

  // Add + icon so can upload graphic
  if (node.type === "building" || node.type === "level") {
    buttons = <LevelHover node={node} />;
  }

  // Add download and delete icon
  if (node.type === "scadaGraphic" && node.getId() && node.parent?.getId()) {
    buttons = <ScadaGraphicHover node={node} />;
  }

  if (
    featureFlags.isFeatureEnabled("graphic-categories") &&
    node.isGraphicItem() &&
    node.path
  ) {
    const categories = (node.categories || []) as GraphicCategoryType[];
    if (categories.length) {
      trailing = (
        <CategoryBadge>
          {categories.map((category) => (
            <CategoryIconPlaceHolder key={category}>
              <CategoryIcon iconType={category} />
            </CategoryIconPlaceHolder>
          ))}
        </CategoryBadge>
      );
    }
    buttons = <GraphicItemHover node={node} />;
  }

  return { buttons, trailing, tooltip };
};

// TODO: remove once given icons are available in miranda design system
// Icon path are copied from the figma as of now.
function Icon({ icon }: { icon: "heat" | "snow" | "fan" | "tune" }) {
  const iconMap = {
    fan: (
      <path
        d="M12 12C12 10.4833 12.5375 9.1875 13.6125 8.1125C14.6875 7.0375 15.9833 6.5 17.5 6.5C19.0167 6.5 20.3125 7.0375 21.3875 8.1125C22.4625 9.1875 23 10.4833 23 12H12ZM2.6125 15.8875C1.5375 14.8125 1 13.5167 1 12H12C12 13.5167 11.4625 14.8125 10.3875 15.8875C9.3125 16.9625 8.01667 17.5 6.5 17.5C4.98333 17.5 3.6875 16.9625 2.6125 15.8875ZM12 12C10.4833 12 9.1875 11.4625 8.1125 10.3875C7.0375 9.3125 6.5 8.01667 6.5 6.5C6.5 4.98333 7.0375 3.6875 8.1125 2.6125C9.1875 1.5375 10.4833 1 12 1V12ZM12 23V12C13.5167 12 14.8125 12.5375 15.8875 13.6125C16.9625 14.6875 17.5 15.9833 17.5 17.5C17.5 19.0167 16.9625 20.3125 15.8875 21.3875C14.8125 22.4625 13.5167 23 12 23Z"
        fill="black"
      />
    ),

    heat: (
      <path
        d="M13.3 20.725C13.2 20.8083 13.0958 20.875 12.9875 20.925C12.8792 20.975 12.7583 21 12.625 21C12.4917 21 12.3583 20.9708 12.225 20.9125C12.0917 20.8542 11.975 20.775 11.875 20.675C11.1917 19.9417 10.6875 19.1917 10.3625 18.425C10.0375 17.6583 9.875 16.85 9.875 16C9.875 15.3833 9.96667 14.7333 10.15 14.05C10.3333 13.3667 10.65 12.4833 11.1 11.4C11.4833 10.45 11.75 9.72083 11.9 9.2125C12.05 8.70417 12.125 8.23333 12.125 7.8C12.125 7.23333 12 6.70417 11.75 6.2125C11.5 5.72083 11.1083 5.225 10.575 4.725C10.475 4.625 10.3958 4.50833 10.3375 4.375C10.2792 4.24167 10.25 4.10833 10.25 3.975C10.25 3.84167 10.275 3.72083 10.325 3.6125C10.375 3.50417 10.4417 3.4 10.525 3.3C10.625 3.2 10.7375 3.125 10.8625 3.075C10.9875 3.025 11.1167 3 11.25 3C11.3833 3 11.5083 3.025 11.625 3.075C11.7417 3.125 11.85 3.19167 11.95 3.275C12.6833 3.95833 13.2292 4.675 13.5875 5.425C13.9458 6.175 14.125 6.96667 14.125 7.8C14.125 8.38333 14.0375 8.99583 13.8625 9.6375C13.6875 10.2792 13.3833 11.1167 12.95 12.15C12.5333 13.15 12.25 13.9167 12.1 14.45C11.95 14.9833 11.875 15.4917 11.875 15.975C11.875 16.5583 11.9958 17.1208 12.2375 17.6625C12.4792 18.2042 12.85 18.75 13.35 19.3C13.4333 19.4 13.5 19.5083 13.55 19.625C13.6 19.7417 13.625 19.8667 13.625 20C13.625 20.1333 13.6 20.2625 13.55 20.3875C13.5 20.5125 13.4167 20.625 13.3 20.725ZM18.175 20.725C18.075 20.8083 17.9708 20.875 17.8625 20.925C17.7542 20.975 17.6333 21 17.5 21C17.3667 21 17.2333 20.9708 17.1 20.9125C16.9667 20.8542 16.85 20.775 16.75 20.675C16.0667 19.9417 15.5625 19.1958 15.2375 18.4375C14.9125 17.6792 14.75 16.875 14.75 16.025C14.75 15.4083 14.8417 14.75 15.025 14.05C15.2083 13.35 15.525 12.4667 15.975 11.4C16.3583 10.45 16.625 9.725 16.775 9.225C16.925 8.725 17 8.25833 17 7.825C17 7.25833 16.875 6.72083 16.625 6.2125C16.375 5.70417 15.9833 5.20833 15.45 4.725C15.35 4.625 15.275 4.5125 15.225 4.3875C15.175 4.2625 15.15 4.13333 15.15 4C15.15 3.86667 15.1708 3.74583 15.2125 3.6375C15.2542 3.52917 15.3167 3.425 15.4 3.325C15.5 3.225 15.6167 3.14583 15.75 3.0875C15.8833 3.02917 16.0167 3 16.15 3C16.2833 3 16.4042 3.025 16.5125 3.075C16.6208 3.125 16.725 3.19167 16.825 3.275C17.5583 3.95833 18.1042 4.675 18.4625 5.425C18.8208 6.175 19 6.96667 19 7.8C19 8.38333 18.9125 8.99583 18.7375 9.6375C18.5625 10.2792 18.2583 11.125 17.825 12.175C17.4083 13.175 17.125 13.9417 16.975 14.475C16.825 15.0083 16.75 15.5083 16.75 15.975C16.75 16.5583 16.875 17.1292 17.125 17.6875C17.375 18.2458 17.75 18.7917 18.25 19.325C18.3333 19.425 18.3958 19.5333 18.4375 19.65C18.4792 19.7667 18.5 19.8833 18.5 20C18.5 20.1333 18.475 20.2667 18.425 20.4C18.375 20.5333 18.2917 20.6417 18.175 20.725ZM8.425 20.725C8.325 20.8083 8.22083 20.875 8.1125 20.925C8.00417 20.975 7.88333 21 7.75 21C7.61667 21 7.48333 20.9708 7.35 20.9125C7.21667 20.8542 7.1 20.775 7 20.675C6.31667 19.9417 5.8125 19.1958 5.4875 18.4375C5.1625 17.6792 5 16.875 5 16.025C5 15.4083 5.09167 14.75 5.275 14.05C5.45833 13.35 5.775 12.4667 6.225 11.4C6.60833 10.45 6.875 9.725 7.025 9.225C7.175 8.725 7.25 8.25833 7.25 7.825C7.25 7.25833 7.125 6.72083 6.875 6.2125C6.625 5.70417 6.23333 5.20833 5.7 4.725C5.58333 4.625 5.5 4.5125 5.45 4.3875C5.4 4.2625 5.375 4.13333 5.375 4C5.375 3.86667 5.4 3.74167 5.45 3.625C5.5 3.50833 5.56667 3.4 5.65 3.3C5.75 3.2 5.8625 3.125 5.9875 3.075C6.1125 3.025 6.24167 3 6.375 3C6.50833 3 6.63333 3.025 6.75 3.075C6.86667 3.125 6.975 3.19167 7.075 3.275C7.80833 3.95833 8.35417 4.67083 8.7125 5.4125C9.07083 6.15417 9.25 6.95 9.25 7.8C9.25 8.38333 9.16667 8.99583 9 9.6375C8.83333 10.2792 8.53333 11.1167 8.1 12.15C7.68333 13.15 7.4 13.9167 7.25 14.45C7.1 14.9833 7.025 15.4917 7.025 15.975C7.025 16.5583 7.14583 17.1292 7.3875 17.6875C7.62917 18.2458 8 18.7917 8.5 19.325C8.58333 19.425 8.64583 19.5333 8.6875 19.65C8.72917 19.7667 8.75 19.8833 8.75 20C8.75 20.1333 8.725 20.2667 8.675 20.4C8.625 20.5333 8.54167 20.6417 8.425 20.725Z"
        fill="black"
      />
    ),

    snow: (
      <path
        d="M11 17.85L8.425 20.375C8.24167 20.5583 8.0125 20.65 7.7375 20.65C7.4625 20.65 7.23333 20.55 7.05 20.35C6.85 20.1667 6.75 19.9375 6.75 19.6625C6.75 19.3875 6.85 19.15 7.05 18.95L11 15V13H9L5.025 16.975C4.84167 17.1583 4.6125 17.25 4.3375 17.25C4.0625 17.25 3.825 17.15 3.625 16.95C3.44167 16.7667 3.35 16.5375 3.35 16.2625C3.35 15.9875 3.44167 15.7583 3.625 15.575L6.15 13H2.975C2.69167 13 2.45833 12.9042 2.275 12.7125C2.09167 12.5208 2 12.2833 2 12C2 11.7167 2.09583 11.4792 2.2875 11.2875C2.47917 11.0958 2.71667 11 3 11H6.15L3.625 8.45C3.44167 8.26667 3.35 8.0375 3.35 7.7625C3.35 7.4875 3.45 7.25 3.65 7.05C3.83333 6.86667 4.0625 6.775 4.3375 6.775C4.6125 6.775 4.85 6.86667 5.05 7.05L9 11H11V9L7.025 5.05C6.84167 4.86667 6.75 4.6375 6.75 4.3625C6.75 4.0875 6.85 3.85 7.05 3.65C7.23333 3.46667 7.4625 3.375 7.7375 3.375C8.0125 3.375 8.24167 3.46667 8.425 3.65L11 6.15V3C11 2.71667 11.0958 2.47917 11.2875 2.2875C11.4792 2.09583 11.7167 2 12 2C12.2833 2 12.5208 2.09583 12.7125 2.2875C12.9042 2.47917 13 2.71667 13 3V6.15L15.55 3.65C15.7333 3.46667 15.9625 3.375 16.2375 3.375C16.5125 3.375 16.75 3.46667 16.95 3.65C17.1333 3.85 17.225 4.0875 17.225 4.3625C17.225 4.6375 17.1333 4.86667 16.95 5.05L13 9V11H15L18.95 7.05C19.1333 6.86667 19.3625 6.775 19.6375 6.775C19.9125 6.775 20.15 6.875 20.35 7.075C20.5333 7.25833 20.625 7.4875 20.625 7.7625C20.625 8.0375 20.5333 8.26667 20.35 8.45L17.85 11H21C21.2833 11 21.5208 11.0958 21.7125 11.2875C21.9042 11.4792 22 11.7167 22 12C22 12.2833 21.9042 12.5208 21.7125 12.7125C21.5208 12.9042 21.2833 13 21 13H17.85L20.35 15.575C20.5333 15.7583 20.625 15.9875 20.625 16.2625C20.625 16.5375 20.5333 16.7667 20.35 16.95C20.15 17.15 19.9125 17.25 19.6375 17.25C19.3625 17.25 19.1333 17.15 18.95 16.95L15 13H13V15L16.95 18.975C17.1333 19.1583 17.225 19.3875 17.225 19.6625C17.225 19.9375 17.125 20.175 16.925 20.375C16.7417 20.5583 16.5125 20.65 16.2375 20.65C15.9625 20.65 15.7333 20.5583 15.55 20.375L13 17.85V21.025C13 21.3083 12.9042 21.5417 12.7125 21.725C12.5208 21.9083 12.2833 22 12 22C11.7167 22 11.4792 21.9042 11.2875 21.7125C11.0958 21.5208 11 21.2833 11 21V17.85Z"
        fill="black"
      />
    ),

    tune: (
      <path
        d="M15.0875 19.1625C14.3625 18.4375 14 17.55 14 16.5C14 15.45 14.3625 14.5625 15.0875 13.8375C15.8125 13.1125 16.7 12.75 17.75 12.75C18.8 12.75 19.6875 13.1125 20.4125 13.8375C21.1375 14.5625 21.5 15.45 21.5 16.5C21.5 17.55 21.1375 18.4375 20.4125 19.1625C19.6875 19.8875 18.8 20.25 17.75 20.25C16.7 20.25 15.8125 19.8875 15.0875 19.1625ZM18.9875 17.7375C19.3292 17.3958 19.5 16.9833 19.5 16.5C19.5 16.0167 19.3292 15.6042 18.9875 15.2625C18.6458 14.9208 18.2333 14.75 17.75 14.75C17.2667 14.75 16.8542 14.9208 16.5125 15.2625C16.1708 15.6042 16 16.0167 16 16.5C16 16.9833 16.1708 17.3958 16.5125 17.7375C16.8542 18.0792 17.2667 18.25 17.75 18.25C18.2333 18.25 18.6458 18.0792 18.9875 17.7375ZM11 17.5H5C4.71667 17.5 4.47917 17.4042 4.2875 17.2125C4.09583 17.0208 4 16.7833 4 16.5C4 16.2167 4.09583 15.9792 4.2875 15.7875C4.47917 15.5958 4.71667 15.5 5 15.5H11C11.2833 15.5 11.5208 15.5958 11.7125 15.7875C11.9042 15.9792 12 16.2167 12 16.5C12 16.7833 11.9042 17.0208 11.7125 17.2125C11.5208 17.4042 11.2833 17.5 11 17.5ZM3.5875 10.1625C2.8625 9.4375 2.5 8.55 2.5 7.5C2.5 6.45 2.8625 5.5625 3.5875 4.8375C4.3125 4.1125 5.2 3.75 6.25 3.75C7.3 3.75 8.1875 4.1125 8.9125 4.8375C9.6375 5.5625 10 6.45 10 7.5C10 8.55 9.6375 9.4375 8.9125 10.1625C8.1875 10.8875 7.3 11.25 6.25 11.25C5.2 11.25 4.3125 10.8875 3.5875 10.1625ZM7.4875 8.7375C7.82917 8.39583 8 7.98333 8 7.5C8 7.01667 7.82917 6.60417 7.4875 6.2625C7.14583 5.92083 6.73333 5.75 6.25 5.75C5.76667 5.75 5.35417 5.92083 5.0125 6.2625C4.67083 6.60417 4.5 7.01667 4.5 7.5C4.5 7.98333 4.67083 8.39583 5.0125 8.7375C5.35417 9.07917 5.76667 9.25 6.25 9.25C6.73333 9.25 7.14583 9.07917 7.4875 8.7375ZM19 8.5H13C12.7167 8.5 12.4792 8.40417 12.2875 8.2125C12.0958 8.02083 12 7.78333 12 7.5C12 7.21667 12.0958 6.97917 12.2875 6.7875C12.4792 6.59583 12.7167 6.5 13 6.5H19C19.2833 6.5 19.5208 6.59583 19.7125 6.7875C19.9042 6.97917 20 7.21667 20 7.5C20 7.78333 19.9042 8.02083 19.7125 8.2125C19.5208 8.40417 19.2833 8.5 19 8.5Z"
        fill="black"
      />
    ),
  };
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {iconMap[icon]}
    </svg>
  );
}

export function CategoryIcon({ iconType }: { iconType: GraphicCategoryType }) {
  switch (iconType) {
    case GraphicCategoryType.Heating:
      return <Icon icon="heat" />;
    case GraphicCategoryType.Cooling:
      return <Icon icon="snow" />;
    case GraphicCategoryType.Ventilation:
      return <Icon icon="fan" />;
    case GraphicCategoryType.Other:
      return <Icon icon="tune" />;
  }
}

function DeviceHover({
  node,
}: {
  node: DeviceTreeNode;
}): ReactElement<XSmallIconButtonProps> | undefined {
  const { user } = useUserContext();
  const { companyId } = useRequiredParams({ companyId: "string" });
  if (
    !hasPermission(Permissions.DEVICES_VIEW, user?.permissions ?? []) ||
    !hasDeviceCapability(node.deviceType, DeviceCapabilities.RemoteAccess)
  ) {
    return undefined;
  }
  return (
    <Link
      to={getDeviceBelindaUrl(node.getId(), companyId).href}
      target="_blank"
    >
      <IconButton
        style={{ marginLeft: "auto" }}
        icon="externalLink"
        size="small"
        shape="xsmall"
      />
    </Link>
  );
}

function LevelHover({
  node,
}: {
  node: TreeNode;
}): ReactElement<XSmallIconButtonProps> | undefined {
  const navigate = useNavigate();
  const { to } = useNavigatonPath();
  const { user } = useUserContext();

  if (!hasPermission(Permissions.SCADAGRAPHICS_EDIT, user?.permissions ?? [])) {
    return undefined;
  }
  return (
    <IconButton
      style={{ marginLeft: "auto" }}
      icon="plus"
      size="small"
      shape="xsmall"
      onPress={() => navigate(to(`graphics/${node.getId()}/add-graphics`))}
    />
  );
}

function ScadaGraphicHover({
  node,
}: {
  node: TreeNode;
}): ReactElement<{ children: ReactNode }> | undefined {
  const [isDownloading, setIsDownloading] = useState(false);
  const { user } = useUserContext();

  if (!hasPermission(Permissions.SCADAGRAPHICS_EDIT, user?.permissions ?? [])) {
    return undefined;
  }

  return (
    <ScadaGraphicWrapper>
      <DownloadGraphics
        graphicsId={node.getId()!}
        name={node.name}
        isDownloading={isDownloading}
        setIsDownloading={setIsDownloading}
      />
      <DeleteGraphics node={node} isDownloading={isDownloading} />
    </ScadaGraphicWrapper>
  );
}

function GraphicItemHover({
  node,
}: {
  node: TreeNode;
}): ReactElement<{ children: ReactNode }> | undefined {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { user } = useUserContext();
  const { clearSelection } = useTreeMethodsContext();

  if (!hasPermission(Permissions.SCADAGRAPHICS_EDIT, user?.permissions ?? [])) {
    return undefined;
  }

  const graphicsId = node.getGraphicsId();
  const menuItemId = Number(node.treeId.split(".")[2]);
  if (!graphicsId || !Number.isInteger(menuItemId)) {
    return undefined;
  }

  return (
    <ScadaGraphicWrapper>
      <Tooltip content={t`Add categories`}>
        <Tooltip.Trigger>
          <CategoryIconButton
            // TODO: Add Tag icon for add category once available in miranda ds
            icon="settings"
            size="small"
            shape="xsmall"
            variant="transparent"
            onPress={() => setIsDialogOpen(true)}
          />
        </Tooltip.Trigger>
      </Tooltip>
      {isDialogOpen && (
        <ApplyProcessTypeDialog
          treeId={node.treeId}
          initialCategories={node.categories}
          onClose={() => setIsDialogOpen(false)}
          onSuccess={() => {
            clearSelection();
          }}
        />
      )}
    </ScadaGraphicWrapper>
  );
}

const ScadaGraphicWrapper = styled.div`
  display: flex;
  margin-left: auto;
  gap: ${(p) => p.theme.space("xsmall")};
`;

const CategoryIconButton = styled(IconButton)`
  margin-left: auto;
  border: 1px dashed ${(p) => p.theme.colors.primary};
  border-radius: ${(p) => p.theme.radius("small")};
`;

const CategoryBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${(p) => p.theme.space("xxsmall")};
  padding: ${(p) => p.theme.space("xxsmall")} ${(p) => p.theme.space("xsmall")};
  border-radius: ${(p) => p.theme.radius("small")};
  background-color: ${(p) => p.theme.colors.neutralBackground};
  color: ${(p) => p.theme.colors.primary};
  flex-shrink: 0;
`;

const CategoryIconPlaceHolder = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;
